import { useCallback, useState, useEffect } from 'react';
import { Box } from 'src/components/UI/Box';
import { Flex } from 'src/components/UI/Flex';
import Typography from 'src/components/Typography';
import { useIsMobile } from 'src/helpers/useIsMobile';
import { usePrescriberContext } from 'src/providers/PrescriberProvider';
import { useNavigate } from 'react-router-dom';
import { BasicAbstractBackground } from 'src/components/BasicAbstractLayout';
import { Tracker } from 'src/shared/tracker/Tracker';
import { TutorialTag } from 'src/components/Helpers/TutorialTag';
import { AnimatePresence, motion } from 'framer-motion';
import { scrollToContainer } from 'src/helpers/window';
import { PatientSearchList } from 'src/components/PatientSearchList';
import PrescriberUpdateDataWarning from 'src/components/PrescriberUpdateDataWarning';
import { DialogWrappedComponentProps, useFeedbackContext } from 'src/providers/FeedbackProvider';
import { ImportPatientsDialog } from 'src/containers/ManagePatients/ImportPatientsDialog';
import { ConfirmDeleteDialog } from 'src/components/ConfirmDeleteDialog';
import { Patient, PatientService } from 'src/services/patient';
import { useMediaQuery } from '@chakra-ui/react';
import {
  trackImportPatientsClick,
  trackNewPatientClick,
  trackPatientCardClick,
  trackPatientDeleteCancelClick,
  trackPatientDeleteClick,
  trackPatientDeleteConfirmClick,
  trackPatientEditClick,
  trackPrescriptionHomeWithPatientsView,
} from 'src/services/tracking/events';
import { OnboardingService } from 'src/services/onboarding';

export function Home() {
  const {
    startPrescrition,
    clickOnFirstPatientFromList,
    isUserInvalid,
    prescriberId,
    patients,
    setPatients,
    setUserDoesNotHaveAnyPatients,
  } = usePrescriberContext();
  const { userGroup } = usePrescriberContext();
  const [lessThanMediumScreen] = useMediaQuery('(max-width: 1200px)');

  const [shouldHideHeader, setShouldHideHeader] = useState(false);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { openBaseDialog, openToast } = useFeedbackContext();

  useEffect(() => {
    if (patients && patients.length > 0) {
      trackPrescriptionHomeWithPatientsView(prescriberId || undefined);
    }
  }, [patients, prescriberId]);

  useEffect(() => {
    if (!prescriberId) return;

    const checkOnboarding = async () => {
      try {
        const response = await OnboardingService.listUnfinished();
        const mainOnboarding = response.data.find(
          (item) => item.onboarding === 'MAIN' && item.status === 'NOT_STARTED'
        );

        if (mainOnboarding) {
          sessionStorage.setItem('onboarding-id', mainOnboarding._id);
          navigate('/onboarding');
        }
      } catch (error) {
        console.error('Erro ao verificar onboarding:', error);
      }
    };

    checkOnboarding();
  }, [navigate, prescriberId]);

  const handleCreatePatient = () => {
    trackNewPatientClick(prescriberId || undefined);

    Tracker.trackEvent({
      event: 'Cadastrar Paciente',
      parameters: {
        data: new Date(),
      },
    });

    scrollToContainer({ containerId: 'mobile-layout-body' });
    navigate('/inicio/cadastro-completo-paciente');
  };

  const handleImportPatient = useCallback(async () => {
    trackImportPatientsClick(prescriberId || undefined);

    if (!prescriberId) {
      openToast('Não foi possível identificar o prescritor. Tente novamente mais tarde.', { type: 'error' });
      return;
    }
    await openBaseDialog(
      (dialogProps: DialogWrappedComponentProps) => (
        <ImportPatientsDialog onClose={dialogProps.onClose} prescriberId={prescriberId} />
      ),
      {
        width: '400px',
        height: 'auto',
      }
    );
  }, [openBaseDialog, prescriberId, openToast]);

  const onDeletePatient = useCallback(
    async (patientId: string) => {
      try {
        if (!prescriberId) return;
        await PatientService.deletePatient(prescriberId, patientId);

        if (!patients) return;

        const updatedPatients = patients.filter((patient: Patient) => patient._id !== patientId);

        if (updatedPatients.length === 0) {
          setUserDoesNotHaveAnyPatients(true);
        }

        setPatients(updatedPatients);
      } catch (error) {
        openToast('Erro ao excluir paciente. Tente novamente em instantes.', { type: 'error', duration: 2000 });
      }
    },
    [prescriberId, patients, setPatients, setUserDoesNotHaveAnyPatients, openToast]
  );

  const handleDeletePatient = useCallback(
    async (patientId: string) => {
      trackPatientDeleteClick(prescriberId || undefined, patientId);

      await openBaseDialog(
        (dialogProps: DialogWrappedComponentProps) => (
          <Box width={'100%'} h={'100%'} display={'flex'} justifyContent={'center'} alignItems={'center'}>
            <ConfirmDeleteDialog
              text="Ao escolher excluir, o cadastro deste paciente irá desaparecer permanentemente da sua base de pacientes."
              onCancel={() => {
                trackPatientDeleteCancelClick(prescriberId || undefined, patientId);
                dialogProps.onClose();
              }}
              onConfirm={async () => {
                trackPatientDeleteConfirmClick(prescriberId || undefined, patientId);
                await onDeletePatient(patientId);
                dialogProps.onClose();
              }}
            />
          </Box>
        ),
        {
          width: '270px',
          height: '356px',
        }
      );
    },
    [onDeletePatient, openBaseDialog, prescriberId]
  );

  const onEditPatient = useCallback(
    (patientId: string) => {
      trackPatientEditClick(prescriberId || undefined, patientId);

      Tracker.trackEvent({
        event: 'Editar Paciente',
        parameters: {
          data: new Date(),
        },
      });
      navigate(`/inicio/editar-paciente/${patientId}`);
    },
    [navigate, prescriberId]
  );

  if (isUserInvalid) {
    return <PrescriberUpdateDataWarning />;
  }

  return (
    <BasicAbstractBackground>
      <Flex flexDirection="column" w="100%" justifyContent={'center'} alignItems={'center'}>
        <AnimatePresence>
          {!shouldHideHeader && (
            <motion.div
              initial={{
                opacity: 0,
              }}
              animate={{
                opacity: 1,
              }}
              exit={{
                opacity: 0,
                height: 0,
              }}
              style={{ display: 'flex', width: '100%', maxWidth: '1200px' }}
            >
              <Box mb={'20px'} maxW={'1200px'} w={'100%'} px={lessThanMediumScreen ? '0px' : '150px'}>
                <Box mb={'10px'} w={'100%'}>
                  <Typography model="h1" weight="bold" textAlign={'left'}>
                    Inicie selecionando um paciente.
                  </Typography>
                </Box>
                <Typography model="body" size={'large'} weight="regular" textAlign={'left'}>
                  Selecione ou busque para qual paciente você deseja iniciar uma prescrição.
                </Typography>
              </Box>
            </motion.div>
          )}
        </AnimatePresence>

        <PatientSearchList
          onCreatePatient={handleCreatePatient}
          onImportPatient={handleImportPatient}
          onEnterCallback={clickOnFirstPatientFromList}
          onFocus={() => isMobile && setShouldHideHeader(true)}
          onBlur={() => setShouldHideHeader(false)}
          onClickCallback={async (patient) => {
            trackPatientCardClick(prescriberId || undefined, patient._id);

            Tracker.trackEvent({
              event: 'Receita Iniciada',
              parameters: {
                data: new Date(),
              },
            });

            if (userGroup === 'prescriber') {
              await startPrescrition(patient._id);
            }
          }}
          showActionButtons={true}
          onDelete={handleDeletePatient}
          onEdit={onEditPatient}
          userGroup={userGroup}
        />
      </Flex>

      {!isMobile && <TutorialTag />}
    </BasicAbstractBackground>
  );
}
